import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

const Profile = () => {
    const { user, isLoading, isError, isSuccess } = useSelector((state: RootState) => state.auth)
    const [formData, setFormData] = useState({
        username: '',
        about: '',
        firstName: '',
        lastName: '',
        email: '',
        country: '',
        address: '',
        city: '',
        state: '',
        postalCode: '',
        notifications: {
            comments: true,
            candidates: false,
            offers: false,
        },
        pushNotifications: 'everything',
    });

    useEffect(() => {
        if (user) {
            setFormData({
                username: user?.username || '',
                about: user?.about || '',
                firstName: user?.first_name || '',
                lastName: user?.last_name || '',
                email: user?.email || '',
                country: user?.country || '',
                address: user?.address || '',
                city: user?.city || '',
                state: user?.state || '',
                postalCode: user?.postalCode || '',
                notifications: user?.notifications || {
                    comments: true,
                    candidates: false,
                    offers: false,
                },
                pushNotifications: user?.pushNotifications || 'everything',
            });
        }
    }, [user]);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setFormData((prev) => ({
                ...prev,
                notifications: { ...prev.notifications, [name]: checked },
            }));
        } else if (type === 'radio') {
            setFormData((prev) => ({ ...prev, pushNotifications: value }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const response = await fetch('/api/profile', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
        } catch (error) {
            console.error('Error saving profile:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-12">
            {/* Profile Section */}
            <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base font-semibold text-gray-900">Profile</h2>
                <p className="mt-1 text-sm text-gray-600">
                    This information will be displayed publicly, so be careful what you share.
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                    <div className="sm:col-span-4">
                        <label htmlFor="username" className="block text-sm font-medium text-gray-900">
                            Username
                        </label>
                        <div className="mt-2">
                            <input
                                id="username"
                                name="username"
                                type="text"
                                value={formData.username}
                                onChange={handleChange}
                                placeholder="janesmith"
                                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-green-600 sm:text-sm"
                            />
                        </div>
                    </div>

                    <div className="col-span-full">
                        <label htmlFor="about" className="block text-sm font-medium text-gray-900">
                            About
                        </label>
                        <textarea
                            id="about"
                            name="about"
                            value={formData.about}
                            onChange={handleChange}
                            rows={3}
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-green-600 sm:text-sm"
                        />
                        <p className="mt-3 text-sm text-gray-600">Write a few sentences about yourself.</p>
                    </div>
                </div>
            </div>

            {/* Personal Information Section */}
            <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base font-semibold text-gray-900">Personal Information</h2>
                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                    <div className="sm:col-span-3">
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-900">
                            First Name
                        </label>
                        <input
                            id="firstName"
                            name="firstName"
                            type="text"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-green-600 sm:text-sm"
                        />
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-900">
                            Last Name
                        </label>
                        <input
                            id="lastName"
                            name="lastName"
                            type="text"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-green-600 sm:text-sm"
                        />
                    </div>

                    <div className="sm:col-span-4">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                            Email
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-green-600 sm:text-sm"
                        />
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="country" className="block text-sm font-medium text-gray-900">
                            Country
                        </label>
                        <select
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-green-600 sm:text-sm"
                        >
                            <option value="">Select Country</option>
                            <option value="United States">United States</option>
                            <option value="Canada">Canada</option>
                            <option value="Mexico">Mexico</option>
                        </select>
                    </div>

                    <div className="col-span-full">
                        <label htmlFor="address" className="block text-sm font-medium text-gray-900">
                            Street Address
                        </label>
                        <input
                            id="address"
                            name="address"
                            type="text"
                            value={formData.address}
                            onChange={handleChange}
                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-green-600 sm:text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Notifications Section */}
            <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base font-semibold text-gray-900">Notifications</h2>
                <fieldset className="mt-6">
                    <legend className="text-sm font-medium text-gray-900">Email Notifications</legend>
                    {['comments', 'candidates', 'offers'].map((type) => (
                        <div key={type} className="mt-4 flex items-center">
                            <input
                                id={type}
                                name={type}
                                type="checkbox"
                                checked={formData.notifications[type]}
                                onChange={handleChange}
                                className="rounded border-gray-300 text-green-600"
                            />
                            <label htmlFor={type} className="ml-3 text-sm text-gray-900">
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </label>
                        </div>
                    ))}
                </fieldset>
            </div>

            {/* Submit Buttons */}
            <div className="mt-6 flex items-center justify-end gap-x-6">
                <button type="button" className="text-sm font-semibold text-gray-900">
                    Cancel
                </button>
                <button
                    type="submit"
                    className="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-500"
                >
                    Update
                </button>
            </div>
        </form>
    );
}

export default Profile